<template>
  <div class="PLPTable__card">
    <table
      v-if="assetsArrRes.length > 0"
      class="table table-hover"
      aria-describedby="plp_table_name"
    >
      <thead>
        <tr>
          <th scope="col" role="button" @click="setSortBy('name')">
            Coin
            <AssetTableSortIcon column="name" />
          </th>
          <th
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('price')"
          >
            {{ $t("global.price") }}
            <AssetTableSortIcon column="price" />
          </th>
          <th
            v-if="!showHistory && !isMobile"
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('sum_total')"
          >
            {{ $t("profitloss.investment") }}
            <AssetTableSortIcon column="sum_total" />
          </th>
          <th
            v-if="!showHistory && !isMobile"
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('assetPrice_avg')"
          >
            {{ $t("profitloss.average") }}
            <AssetTableSortIcon column="assetPrice_avg" />
          </th>
          <th
            v-if="!showHistory && !isMobile"
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('percChangeHour')"
          >
            {{ $t("global.hour") }}
            <AssetTableSortIcon column="percChangeHour" />
          </th>
          <th
            v-if="!showHistory && (!isMobile || isLandscape)"
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('percChangeDay')"
          >
            {{ $t("global.day") }}
            <AssetTableSortIcon column="percChangeDay" />
          </th>
          <th
            v-if="!showHistory && !isMobile"
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('percChangeWeek')"
          >
            {{ $t("global.week") }}
            <AssetTableSortIcon column="percChangeWeek" />
          </th>
          <th
            scope="col"
            role="button"
            class="text-center"
            @click="setSortBy('pl_total')"
          >
            {{ isMobile ? $t("profitloss.pl") : $t("profitloss.profitloss") }}
            <AssetTableSortIcon column="pl_total" />
          </th>
          <th scope="col" class="text-center px-0">
            {{ isMobile ? "" : $t("global.notes") }}
          </th>
          <th scope="col" class="text-right" :colspan="isMobile ? 2 : 1">
            {{ $t("profitloss.transactions") }}
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="asset in assetsArrRes">
          <tr class="PLPTable__Row" :key="asset.uniqueId">
            <td
              :class="[
                'PLPTable__AssetName',
                asset.isDeleted ? 'PLPTable__AssetName_deleted' : '',
              ]"
              :title="
                asset.name +
                ' ' +
                asset.symbol +
                (asset.isDeleted ? ' (Deleted coin)' : '')
              "
            >
              <AssetIcon
                :asset="asset"
                :size="24"
                class="PLPTable__AssetName__Icon"
              />
              <span v-if="!isMobile">
                {{ asset.name }}
              </span>
              <span class="PLPTable__AssetName__Symbol">{{
                asset.symbol
              }}</span>
              <a
                :href="moreInfoUrl(asset)"
                class="bg-info text-white rounded-circle PLPTable__MoreInfoLink"
                target="_blank"
                ><i class="fa fa-fw fa-info"></i
              ></a>
            </td>
            <td
              class="text-center"
              :class="{ bigStrWrapper: formatMoney(asset.price).length > 12 }"
            >
              <span
                :style="`--bigStrLen: ${formatMoney(asset.price).length}ch;`"
              >
                {{ formatMoney(asset.price) || "-" }}
              </span>
            </td>
            <td
              v-if="!showHistory && !isMobile"
              class="text-center font-weight-normal"
              :class="{
                bigStrWrapper: formatMoney(asset.sum_total).length > 12,
              }"
            >
              <span
                :style="`--bigStrLen: ${
                  formatMoney(asset.sum_total).length
                }ch;`"
              >
                {{ formatMoney(asset.sum_total) || "-" }}
              </span>
            </td>
            <td
              v-if="!showHistory && !isMobile"
              class="text-center font-weight-normal"
              :class="{
                bigStrWrapper: formatMoney(asset.assetPrice_avg).length > 12,
              }"
            >
              <span
                :style="`--bigStrLen: ${
                  formatMoney(asset.assetPrice_avg).length
                }ch;`"
              >
                {{ formatMoney(asset.assetPrice_avg) || "-" }}
              </span>
            </td>
            <td
              v-if="!showHistory && !isMobile"
              :class="[
                'text-center',
                'PLPTable__pl_colored',
                asset.percChangeHour < 0
                  ? 'PLPTable__pl_colored--Negative'
                  : '',
              ]"
            >
              {{ parseFloat(asset.percChangeHour).toFixed(2) + "%" }}
            </td>
            <td
              v-if="!showHistory && (!isMobile || isLandscape)"
              :class="[
                'text-center',
                'PLPTable__pl_colored',
                asset.percChangeDay < 0 ? 'PLPTable__pl_colored--Negative' : '',
              ]"
            >
              {{ parseFloat(asset.percChangeDay).toFixed(2) + "%" }}
            </td>
            <td
              v-if="!showHistory && !isMobile"
              :class="[
                'text-center',
                'PLPTable__pl_colored',
                asset.percChangeWeek < 0
                  ? 'PLPTable__pl_colored--Negative'
                  : '',
              ]"
            >
              {{ parseFloat(asset.percChangeWeek).toFixed(2) + "%" }}
            </td>
            <td
              :class="[
                'text-center',
                'font-weight-normal',
                'PLPTable__pl_colored',
                asset.pl_total < 0 ? 'PLPTable__pl_colored--Negative' : '',
                formatMoney(asset.assetPrice_avg).length > 12
                  ? 'bigStrWrapper'
                  : '',
              ]"
            >
              <span
                :style="`--bigStrLen: ${formatMoney(asset.pl_total).length}ch;`"
              >
                {{ formatMoney(asset.pl_total) || "-" }}
              </span>
            </td>
            
            <td v-if="isMobile" class="text-center px-0 PLPTable__Actions">
              <button
                type="button"
                class="btn btn-link btn-lg mr-1 py-0 px-1 opacity-75"
                @click.prevent="toggleShowExpandInfo(asset)"
              >
                <span
                  class="fa"
                  :class="[
                    showExpandInfo[asset.uniqueId]
                      ? 'fa-compress'
                      : 'fa-expand',
                  ]"
                ></span>
              </button>
            </td>
            <td class="text-center px-0 PLPTable__Actions">
              <AssetTableNotes
                :asset="asset"
                :isLandscape="isLandscape"
                :updateAsset="updateAsset"
                :label="$t('profitloss.asset_notes_label')"
                :placeholder="$t('profitloss.asset_notes_placeholder')"
              />
            </td>
            <td class="text-right PLPTable__Actions">
              <button
                type="button"
                class="btn btn-secondary btn-sm text-white text-nowrap"
                :class="{ 'btn-info': !showTransactions[asset.currencyId] }"
                @click="toggleShowTransaction(asset.currencyId)"
              >
                {{
                  showTransactions[asset.currencyId]
                    ? $t("profitloss.hide")
                    : `${$t("profitloss.show")} (${
                        asset.transactions.filter((v) => v.holdingsLeft).length
                      })`
                }}
              </button>
            </td>
          </tr>
          <tr
            v-if="isMobile && showExpandInfo[asset.uniqueId]"
            :key="`expandInfo_${asset.uniqueId}`"
          >
            <td colspan="99" class="p-0" :class="{ 'bg-light': !nightMode }">
              <div class="PLPTable__SubTableWrapper-info">
                <table>
                  <tbody>
                    <tr>
                      <td>Coin</td>
                      <td>{{ asset.name || asset.currency }}</td>
                    </tr>
                    <tr>
                      <td>{{ $t("global.price") }}</td>
                      <td>{{ formatMoney(asset.price) || "-" }}</td>
                    </tr>
                    <tr>
                      <td>{{ $t("profitloss.investment") }}</td>
                      <td>{{ formatMoney(asset.sum_total) || "-" }}</td>
                    </tr>
                    <tr>
                      <td>{{ $t("profitloss.average") }}</td>
                      <td>
                        {{ formatMoney(asset.assetPrice_avg) || "-" }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ $t("profitloss.profitloss") }}</td>
                      <td
                        class="PLPTable__pl_colored"
                        :class="{
                          'PLPTable__pl_colored--Negative': asset.pl_total < 0,
                        }"
                      >
                        {{ formatMoney(asset.pl_total) }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ $t("global.hour") }}</td>
                      <td
                        class="PLPTable__pl_colored"
                        :class="{
                          'PLPTable__pl_colored--Negative':
                            asset.percChangeHour < 0,
                        }"
                      >
                        {{ parseFloat(asset.percChangeHour).toFixed(2) + "%" }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ $t("global.day") }}</td>
                      <td
                        class="PLPTable__pl_colored"
                        :class="{
                          'PLPTable__pl_colored--Negative':
                            asset.percChangeDay < 0,
                        }"
                      >
                        {{ parseFloat(asset.percChangeDay).toFixed(2) + "%" }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ $t("global.week") }}</td>
                      <td
                        class="PLPTable__pl_colored"
                        :class="{
                          'PLPTable__pl_colored--Negative':
                            asset.percChangeWeek < 0,
                        }"
                      >
                        {{ parseFloat(asset.percChangeWeek).toFixed(2) + "%" }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </tr>
          <tr
            class=""
            v-show="showTransactions[asset.currencyId]"
            :key="`transactions_${asset.uniqueId}`"
          >
            <td colspan="11" class="p-0" :class="{ 'bg-white': !nightMode }">
              <div class="PLPTable__SubTableWrapper">
                <ProfitLossPortfolioTableSubCurrent
                  v-if="!showHistory"
                  :asset="asset"
                  :isMobile="isMobile"
                  :isLandscape="isLandscape"
                  :removeTransactionClick="removeTransactionClick"
                />
                <ProfitLossPortfolioTableSubHistory
                  v-else
                  :asset="asset"
                  :isMobile="isMobile"
                  :isLandscape="isLandscape"
                  :removeTransactionClick="removeTransactionClick"
                />
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div v-if="assetsArrRes.length > 0" class="text-right">
      <a
        href="/"
        class="text-white opacity-50"
        @click.prevent="toggleDeleteAllDialog"
      >
        {{ $t("profitloss.reset_portfolio") }}
      </a>
    </div>
    <b-card v-if="!assetsArrRes.length" class="text-muted">
      {{ $t("profitloss.list_empty") }}
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import MobileMixin from "@/mixins/mobile";
import NumbersMixin from "@/mixins/numbers";
import toast from "@/plugins/toast";
import { sortByTextCol, sortByNumCol } from "@/services/arrays";
import AssetIcon from "./AssetIcon.vue";
import AssetTableEdit from "./AssetTableEdit.vue";
import AssetTableNotes from "./AssetTableNotes.vue";
import AssetTableTrade from "./AssetTableTrade.vue";
import ExchangeIcon from "./ExchangeIcon.vue";
import AddAssetButton from "./AddAssetButton.vue";
import AssetTableSortIcon from "./AssetTableSortIcon.vue";
import ProfitLossPortfolioTableSubCurrent from "./ProfitLossPortfolioTableSubCurrent.vue";
import ProfitLossPortfolioTableSubHistory from "./ProfitLossPortfolioTableSubHistory.vue";

const sortAssets = (sortByObj) => {
  return sortByObj.sortType === "text"
    ? sortByTextCol(sortByObj.name)
    : sortByNumCol(sortByObj.name);
};

export default {
  name: "AssetTable",
  mixins: [MobileMixin, NumbersMixin],
  components: {
    AssetIcon,
    AssetTableEdit,
    AssetTableNotes,
    AssetTableTrade,
    ExchangeIcon,
    AddAssetButton,
    AssetTableSortIcon,
    ProfitLossPortfolioTableSubCurrent,
    ProfitLossPortfolioTableSubHistory,
  },
  data() {
    return {
      showTransactions: {},
      showExpandInfo: {},
    };
  },
  computed: {
    assetsArrRes() {
      const { sortByObj, sortByDirTxt } = this;
      const combined = this.assetsArr(this.showHistory);

      combined.sort(sortAssets(sortByObj));

      if (sortByDirTxt === "desc") {
        return combined.reverse();
      }
      return combined;
    },
    ...mapGetters({
      nightMode: "settings/nightMode",
      showHistory: "profitloss/showHistory",
      assetsArr: "profitloss/assetsArr",
      sortBy: "settings/sortBy",
      sortByObj: "settings/sortByObj",
      sortByDirTxt: "settings/sortByDirTxt",
    }),
  },
  methods: {
    toggleShowTransaction(currencyId) {
      this.showTransactions = {
        ...this.showTransactions,
        [currencyId]: !this.showTransactions[currencyId],
      };
    },
    toggleShowExpandInfo(asset) {
      this.showExpandInfo = {
        ...this.showExpandInfo,
        [asset.uniqueId]: !this.showExpandInfo[asset.uniqueId],
      };
    },
    moreInfoUrl(asset) {
      return `https://coinmarketcap.com/currencies/${asset.currencyId}/`;
    },
    toggleDeleteAllDialog() {
      this.toggleDialog("profitlossDeteleAll");
    },
    setSortBy(column) {
      if (this.sortBy === column) {
        this.toggleSetting("sortByDir");
      } else {
        this.setSetting({ setting: "sortBy", value: column });
        this.setSetting({ setting: "sortByDir", value: true });
      }
    },
    removeTransactionClick(transactionId) {
      const { removeTransaction } = this;
      toast({
        type: "error",
        position: "center",
        message: this.$t("profitloss.delete_transaction_text"),
        timeout: 7000,
        buttons: [
          [
            `<button>${this.$t(
              "edit_asset_page.delete"
            ).toUpperCase()}</button>`,
            function (instance, toastLoc) {
              instance.hide(
                {
                  transitionOut: "fadeOutUp",
                  onClosing: function (_instance, _toastLoc) {
                    removeTransaction(transactionId);
                  },
                },
                toastLoc
              );
            },
            true,
          ], // true to focus
          [
            `<button>${this.$t("exchange.cancel")}</button>`,
            function (instance, toastLoc) {
              instance.hide({ transitionOut: "fadeOutUp" }, toastLoc);
            },
          ],
        ],
      });
    },
    ...mapActions({
      removeTransaction: "profitloss/removeTransaction",
      updateAsset: "profitloss/updateAsset",
      toggleDialog: "ui/toggleDialog",
      setSetting: "settings/setSetting",
      toggleSetting: "settings/toggleSetting",
    }),
  },
};
</script>

<style lang="scss">
@import "~@/scss/mixins";
@import "~@/scss/variables";

.PLPTable__pl_colored {
  color: $color-tertiary;

  &--Negative {
    color: $color-primary;
  }
}

.PLPTable__SubTableWrapper {
  font-size: 90%;
  margin-bottom: 2rem;
  box-shadow: 0 10px 20px -20px black;
  th {
    font-weight: 400;
  }
  td {
    white-space: nowrap;
  }
  @include breakpoint-manual(max-width, $break-small) {
    max-width: calc(100vw - 30px);
    overflow-x: auto;
  }
}
</style>

<style lang="scss" scoped>
@import "~@/scss/mixins";
@import "~@/scss/variables";

.PLPTable__card {
  font-weight: 300;
  position: relative;
  font-size: 18px;
  color: rgba($color-secondary, 0.8);

  tbody {
    box-shadow: 0 3px 10px 0 #030630;
    border-radius: 15px;
  }

  th {
    border: none;
    color: #fff;
    font-weight: 700;
    font-size: 15px;
    white-space: nowrap;

    .fa {
      font-size: 0.85em;
      margin-left: 0.35rem;
    }
  }

  @include breakpoint-manual(max-width, $break-small) {
    font-size: 16px;
  }
}

.PLPTable__Row {
  background: #fff !important;

  td {
    padding: 25px 10px;

    // background: #fff;
    overflow: hidden;

    &:first-child {
      padding-left: 14px;
    }

    &:last-child {
      padding-right: 14px;
    }

    @include breakpoint-manual(max-width, $break-small) {
      vertical-align: middle;
      padding: 15px 10px;
      &:first-child {
        padding-left: 12px;
        padding-right: 4px;
        overflow: visible;
      }
    }
  }

  &:first-child {
    td {
      border-top: none;

      &:first-child {
        border-radius: 15px 0 0 0;
      }

      &:last-child {
        border-radius: 0 15px 0 0;
      }
    }
  }

  &:last-child td {
    &:first-child {
      border-radius: 0 0 0 15px;
    }

    &:last-child {
      border-radius: 0 0 15px;
    }
  }
}
.table-hover tbody tr:hover td {
  background: #f4f4f4;
  transition: background 0.1s linear;
}

.PLPTable__AssetName {
  position: relative;
  font-weight: 700;
  white-space: nowrap;
  max-width: 210px;
  text-overflow: ellipsis;
}

td.PLPTable__AssetName_deleted {
  background-color: rgba(251, 139, 187, 0.3) !important;
}

.PLPTable__AssetName__Icon {
  vertical-align: inherit;
  margin-left: 3px;
  margin-right: 14px;
  line-height: 1;
  @include breakpoint-manual(max-width, $break-small) {
    margin-left: 0;
    margin-right: 13px;
  }
}

.PLPTable__AssetName__ExchIcon {
  position: absolute;
  bottom: 8px;
  left: 27px;
  padding: 2px;
  border-radius: 50%;
  background-color: #fff;
  font-size: 0;
}

.PLPTable__AssetName__Symbol {
  font-weight: 300;
  margin-left: 5px;
  opacity: 0.5;

  @include breakpoint-manual(max-width, $break-small) {
    margin: 0;
    opacity: 0.8;
  }
}

.PLPTable__MoreInfoLink {
  display: inline-block;
  padding: 0;
  text-align: center;

  width: 15px;
  height: 15px;

  position: absolute;
  left: 38px;
  top: 14px;

  font-size: 0.7rem;
  opacity: 0.9;

  @include breakpoint-manual(max-width, $break-small) {
    left: 30px;
    top: 11px;
  }
}

.PLPTable__Actions {
  overflow: visible !important;
}
.PLPTable__SubTableWrapper-info {
  font-size: 90%;
  table {
    width: 100%;
  }
  tbody {
    box-shadow: none;
    border-radius: 0;
  }
  td {
    padding: 0.5em 1em;
    &:last-child {
      font-weight: 400;
    }
  }
}

.bigStrWrapper {
  padding: 0 !important;
  > span {
    display: block;
    margin: 25px 10px;
    width: 11ch;
    max-width: 11ch;
    white-space: nowrap;
    overflow: hidden;
    animation: 2s cubic-bezier(0.65, 0.05, 0.36, 1) 0s infinite alternate
      bigStrAnimation;
  }
}
@keyframes bigStrAnimation {
  from {
    text-indent: 0.5ch;
  }
  to {
    text-indent: calc(11ch - var(--bigStrLen));
  }
}
</style>
